import type {
  DealCaptureActivityRow,
  DealCaptureContractOption,
  DealCaptureDealOption,
  DealCaptureEntityOption,
  DealCaptureLookups,
  DealCaptureOverview,
} from "@/types/deal-capture";

/**
 * STUB DATA — the only place Deal Capture's figures are invented.
 *
 * Deal Capture's API (AB#8980) does not exist yet. Until it does, the landing
 * and the sidebar pickers read these values through the two hooks in
 * `hooks/useDealCapture.ts`, and nothing else imports this file. Swapping to
 * the real API is a change to those hooks' `queryFn`s, after which this file
 * is deleted.
 *
 * The names are made up. The shapes and proportions follow the PowerUp
 * prototype (23 master contracts, a few dozen legal entities).
 */

const ENTITIES: DealCaptureEntityOption[] = [
  { id: "e-01", shortName: "Northwind", legalName: "Northwind Foods LLC", kind: "customer" },
  { id: "e-02", shortName: "Harbor Steel", legalName: "Harbor Steel Works Inc.", kind: "customer" },
  { id: "e-03", shortName: "Pinecrest", legalName: "Pinecrest Medical Center", kind: "customer" },
  { id: "e-04", shortName: "Lakeside Ag", legalName: "Lakeside Agricultural Co-op", kind: "customer" },
  { id: "e-05", shortName: "Granite Data", legalName: "Granite Data Centers LP", kind: "customer" },
  { id: "e-06", shortName: "Riverbend", legalName: "Riverbend School District", kind: "customer" },
  { id: "e-07", shortName: "Summit Cold", legalName: "Summit Cold Storage LLC", kind: "customer" },
  { id: "e-08", shortName: "Copperline", legalName: "Copperline Manufacturing Corp.", kind: "customer" },
  { id: "e-09", shortName: "Bluewater", legalName: "Bluewater Energy Marketing LLC", kind: "trade" },
  { id: "e-10", shortName: "Meridian", legalName: "Meridian Power Trading LP", kind: "trade" },
  { id: "e-11", shortName: "Cascade", legalName: "Cascade Merchant Energy Inc.", kind: "trade" },
  { id: "e-12", shortName: "Ironwood", legalName: "Ironwood Generation LLC", kind: "trade" },
  { id: "e-13", shortName: "Sierra Grid", legalName: "Sierra Grid Services LP", kind: "trade" },
  { id: "e-14", shortName: "Tidewater", legalName: "Tidewater Power Partners LLC", kind: "trade" },
];

const CUSTOMERS = ENTITIES.filter((e) => e.kind === "customer");
const TRADERS = ENTITIES.filter((e) => e.kind === "trade");

const CONTRACT_SELLERS = [0, 1, 2, 3, 4, 5, 0, 1, 2, 3, 4, 5, 0, 1, 2, 3, 4, 5, 0, 1, 2, null, null];

const CONTRACTS: DealCaptureContractOption[] = CONTRACT_SELLERS.map((t, i) => {
  const trader = t === null ? null : TRADERS[t];
  const kind = i % 3 === 2 ? "WSPP" : "EEI";
  return {
    id: `c-${String(i + 1).padStart(2, "0")}`,
    // The sequence keeps every number unique; the trader and kind repeat.
    number: `PPG-${trader?.shortName.replace(/\s+/g, "").toUpperCase() ?? "TBD"}-${kind}-${String(i + 1).padStart(2, "0")}`,
    entity: trader?.legalName ?? null,
    dealCount: 0,
  };
});

const PRODUCTS = ["SP15 on-peak block", "Palo Verde ATC", "Mid-C off-peak", "NP15 shaped", "Mead on-peak"];

const DEALS: DealCaptureDealOption[] = Array.from({ length: 120 }, (_, i) => {
  const buyer = CUSTOMERS[i % CUSTOMERS.length];
  const seller = TRADERS[(i * 7) % TRADERS.length];
  const contract = CONTRACTS[(i * 5) % CONTRACTS.length];
  contract.dealCount += 1;
  return {
    number: String(2002000 + i * 3 + 17),
    // Every fourth deal is non-integrated, so the flow toggle has something to show.
    flow: i % 4 === 3 ? "non_integrated" : "integrated",
    description: `${buyer.shortName} ${PRODUCTS[i % PRODUCTS.length]} ${2025 + (i % 3)}`,
    buyer: buyer.shortName,
    seller: seller.shortName,
  };
});

const INTEGRATED_DEALS = DEALS.filter((d) => d.flow === "integrated").length;

const LOOKUPS: DealCaptureLookups = { deals: DEALS, contracts: CONTRACTS, entities: ENTITIES };

/** A smooth-ish series of `n` points, deterministic so screenshots are stable. */
function series(n: number, base: number, swing: number, trend = 0): number[] {
  return Array.from({ length: n }, (_, i) =>
    Math.max(0, Math.round(base + trend * i + swing * Math.sin(i * 0.9) + (i % 4))),
  );
}

function dealRow(deal: DealCaptureDealOption, i: number): DealCaptureActivityRow {
  const day = 30 - i * 3;
  return {
    id: deal.number,
    item: deal.number,
    detail: deal.description,
    change: i % 3 === 0 ? "created" : "updated",
    ownerOrEntity: ["Dana Whitfield", "Luis Ortega", null][i % 3],
    date: `2026-10-${String(Math.max(day, 1)).padStart(2, "0")}`,
  };
}

function contractRow(contract: DealCaptureContractOption, i: number): DealCaptureActivityRow {
  return {
    id: contract.id,
    item: contract.number,
    detail: `${contract.number.includes("WSPP") ? "WSPP" : "EEI"} · ${contract.dealCount} deals`,
    change: "record_updated",
    ownerOrEntity: contract.entity,
    date: `2026-${String(9 - i).padStart(2, "0")}-15`,
  };
}

const OVERVIEW: DealCaptureOverview = {
  asOf: "2026-10-30",
  counts: {
    deals: DEALS.length,
    contracts: CONTRACTS.length,
    entities: ENTITIES.length,
    deleted: 0,
  },
  cards: [
    {
      area: "deals",
      title: "Deals · Integrated",
      pages: [
        {
          label: "All deals",
          value: INTEGRATED_DEALS,
          unit: "deals",
          sub: `${TRADERS.length} sellers · ${CUSTOMERS.length} buyers`,
          series: series(24, 4, 2, 0.1),
          seriesNote: "New deals per month · last 24 months",
          barLabel: "Active share",
          barShare: 0.62,
          barTone: "good",
          delta: "9 new",
          deltaNote: "last 30 days vs 6 prior",
          deltaTone: "good",
          chips: ["SP15 41", "Palo Verde 27"],
        },
        {
          label: "Active",
          value: 74,
          unit: "delivering now",
          sub: "Across 19 master contracts",
          series: series(24, 60, 6, 0.5),
          seriesNote: "Active deals at each month end · last 24 months",
          barLabel: "1-yr percentile",
          barShare: 0.83,
          barTone: "good",
          delta: "11 ending",
          deltaNote: "in the next 90 days",
          deltaTone: "warn",
          chips: ["SP15 23", "Mid-C 14"],
        },
        {
          label: "Upcoming",
          value: 18,
          unit: "not started",
          sub: "Next start 12/01/2026 · deal 2002317",
          series: series(18, 2, 2),
          seriesNote: "Deals starting per month · next 18 months",
          barLabel: "Share of book",
          barShare: 0.15,
          barTone: "neutral",
          delta: "6 starting",
          deltaNote: "in the next 90 days",
          deltaTone: "good",
          chips: ["NP15 7", "Mead 5"],
        },
        {
          label: "Needs attention",
          value: 13,
          unit: "deals flagged",
          sub: "7 Unknown type · 4 no term · 2 blank quantity",
          series: series(24, 1, 1),
          seriesNote: "Flagged deals by month created · last 24 months",
          barLabel: "Flagged share",
          barShare: 0.11,
          barTone: "warn",
          delta: "7 Unknown",
          deltaNote: "deal type is the largest gap",
          deltaTone: "warn",
          chips: [],
        },
      ],
    },
    {
      area: "contracts",
      title: "Contracts",
      pages: [
        {
          label: "All contracts",
          value: CONTRACTS.length,
          unit: "master contracts",
          sub: `EEI 16 · WSPP 7 · ${DEALS.length} deals`,
          series: [1, 2, 3, 3, 5, 6, 8, 9, 11, 12, 14, 15, 17, 18, 20, 21, 22, 23],
          seriesNote: "Contracts executed, cumulative · 2009 to today",
          barLabel: "With active deals",
          barShare: 0.83,
          barTone: "good",
          delta: "+3 signed",
          deltaNote: "in the last 2 years",
          deltaTone: "good",
          chips: ["EEI 16", "WSPP 7"],
        },
        {
          label: "In use",
          value: 19,
          unit: "with active deals",
          sub: "74 active deals across them",
          series: series(24, 16, 2, 0.1),
          seriesNote: "Contracts with an active deal at each month end · last 24 months",
          barLabel: "1-yr percentile",
          barShare: 0.75,
          barTone: "good",
          delta: "2 dormant",
          deltaNote: "· 2 never used",
          deltaTone: "warn",
          chips: [],
        },
        {
          label: "Concentration",
          value: 22,
          unit: "% of deals on BLUEWATER-EEI-01",
          sub: "Top 3 hold 48% of deals",
          series: [26, 18, 14, 9, 8, 7, 6, 6, 5, 5, 4, 4, 3, 3, 2, 2, 2, 1, 1, 1, 1],
          seriesNote: "Deals per contract, largest to smallest",
          barLabel: "Top 3 share",
          barShare: 0.48,
          barTone: "neutral",
          delta: "MERIDIAN-EEI-02 15%",
          deltaNote: "is second largest",
          deltaTone: "neutral",
          chips: ["BLUEWATER-EEI-01", "MERIDIAN-EEI-02"],
        },
        {
          label: "Upkeep",
          value: 6,
          unit: "not updated in 2+ years",
          sub: "2 placeholder legal entities · no termination dates on file",
          series: [2, 3, 3, 5, 8, 11, 14, 17, 20, 23],
          seriesNote: "Contracts last updated by year, cumulative",
          barLabel: "Stale share",
          barShare: 0.26,
          barTone: "warn",
          delta: "2 placeholders",
          deltaNote: "to fix",
          deltaTone: "warn",
          chips: [],
        },
      ],
    },
    {
      area: "entities",
      title: "Entities",
      pages: [
        {
          label: "All entities",
          value: ENTITIES.length,
          unit: "legal entities",
          sub: `${CUSTOMERS.length} Customer · ${TRADERS.length} Trade · 13 in use`,
          series: [26, 21, 17, 15, 12, 10, 8, 6, 3, 2, 0, 0, 0, 0],
          seriesNote: "Deals per entity, largest to smallest",
          barLabel: "In use",
          barShare: 0.93,
          barTone: "good",
          delta: "0 missing",
          deltaNote: "names on deals with no entity record",
          deltaTone: "good",
          chips: [`Customer ${CUSTOMERS.length}`, `Trade ${TRADERS.length}`],
        },
        {
          label: "Customers",
          value: CUSTOMERS.length,
          unit: "buyers",
          sub: "7 with active deals · 8 with a contract",
          series: series(24, 3, 2),
          seriesNote: "New deals with a Customer buyer per month · last 24 months",
          barLabel: "With a contract",
          barShare: 1,
          barTone: "good",
          delta: "Northwind",
          deltaNote: "leads with 15 deals",
          deltaTone: "neutral",
          chips: [],
        },
        {
          label: "Trade",
          value: TRADERS.length,
          unit: "counterparties",
          sub: "5 selling on active deals",
          series: series(24, 3, 1),
          seriesNote: "New deals with a Trade seller per month · last 24 months",
          barLabel: "Active share",
          barShare: 0.83,
          barTone: "neutral",
          delta: "Bluewater",
          deltaNote: "leads with 20 deals",
          deltaTone: "neutral",
          chips: [],
        },
        {
          label: "Needs attention",
          value: 3,
          unit: "items flagged",
          sub: "1 unused · 2 placeholder",
          series: [1, 0, 2, 0, 0],
          seriesNote: "Flags by check · unused, test, placeholder, no contract, missing",
          barLabel: "Entities flagged",
          barShare: 0.21,
          barTone: "warn",
          delta: "0 to add",
          deltaNote: "every name on a deal has a record",
          deltaTone: "good",
          chips: [],
        },
      ],
    },
  ],
  recentDeals: {
    editedLast30Days: 9,
    rows: DEALS.slice(-8).reverse().map(dealRow),
  },
  recentContracts: CONTRACTS.slice(0, 5).map(contractRow),
};

export const DEAL_CAPTURE_STUB = { overview: OVERVIEW, lookups: LOOKUPS } as const;
